import { useRef, useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Save, Trash2, Loader2 } from "lucide-react";
import SimpleSignaturePad from "@/components/SimpleSignaturePad";
import { usersApi } from "@/lib/api";
import { processSignatureImage } from "@/lib/signatureImage";
import { useToast } from "@/components/SimpleToast";

/**
 * Einstellungen-Bereich: eigene Unterschrift hinterlegen (zeichnen oder Bild
 * hochladen). Wird in Serviceberichten über das Auswahlfenster verwendet.
 */
export function MySignatureSettings() {
  const padRef = useRef(null);
  const fileRef = useRef(null);
  const toast = useToast();
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    usersApi
      .getMySignature()
      .then((res) => setInitial(res.data?.signature || null))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      // Hochgeladenes Bild automatisch zuschneiden (Weißrand entfernen, Hintergrund transparent)
      const processed = await processSignatureImage(reader.result);
      setInitial(processed);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSave = async () => {
    const sig = padRef.current?.getSignature();
    if (!sig) {
      toast.error("Bitte zuerst unterschreiben oder ein Bild hochladen");
      return;
    }
    setSaving(true);
    try {
      await usersApi.setMySignature(sig);
      toast.success("Unterschrift gespeichert");
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await usersApi.setMySignature(null);
      setInitial(null);
      padRef.current?.clear();
      toast.success("Unterschrift entfernt");
    } catch {
      toast.error("Entfernen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meine Unterschrift</CardTitle>
        <CardDescription>
          Hinterlegen Sie Ihre Unterschrift einmalig – sie kann dann in
          Serviceberichten beim Techniker-Feld schnell ausgewählt werden.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <SimpleSignaturePad
          ref={padRef}
          label="Unterschrift"
          initialValue={initial}
          onSignatureChange={() => {}}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
          data-testid="signature-upload-input"
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            data-testid="signature-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" />
            Bild hochladen
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving} data-testid="signature-save-btn">
            <Save className="w-4 h-4 mr-1" />
            Speichern
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={handleRemove}
            disabled={saving}
            className="text-red-600 hover:text-red-700"
            data-testid="signature-remove-btn"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Entfernen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default MySignatureSettings;
